<template>
  <div>
    <h3 class="title-content">Notificações</h3>

    <v-card class="form-card">
      <v-card-text>
        <v-alert
            type="info"
            color="#1976d2"
            outlined
            class="mb-4"
        >
          Personalize como você deseja receber suas notificações
        </v-alert>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular
              indeterminate
              color="primary"
              size="64"
          ></v-progress-circular>
          <p class="mt-4 text--secondary">Carregando preferências...</p>
        </div>

        <div v-else>
          <div
              v-for="(category, categoryIndex) in notificationsConfig"
              :key="categoryIndex"
              class="notification-category"
          >
            <h4 class="category-title">{{ category.name }}</h4>

            <v-divider class="my-3"></v-divider>

            <div
                v-for="(option, optionIndex) in category.options"
                :key="optionIndex"
                class="notification-option"
            >
              <v-checkbox
                  v-model="preferences[option.permission]"
                  :label="option.name"
                  :disabled="updatingPreference === option.permission"
                  :loading="updatingPreference === option.permission"
                  color="primary"
                  hide-details
                  @change="updatePreference(option.permission, $event)"
              >
                <template v-slot:label>
                  <span :class="{ 'text--disabled': updatingPreference === option.permission }">
                    {{ option.name }}
                  </span>
                </template>
              </v-checkbox>

              <v-progress-linear
                  v-if="updatingPreference === option.permission"
                  indeterminate
                  color="primary"
                  height="2"
                  class="mt-1"
              ></v-progress-linear>
            </div>
          </div>

          <v-alert
              v-if="notificationsConfig.length === 0"
              type="warning"
              outlined
              class="mt-4"
          >
            Nenhuma configuração de notificação disponível
          </v-alert>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn
            @click="handleBack"
            class="btn-cancel"
            :disabled="updatingPreference !== null"
        >
          Voltar
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-snackbar
        v-model="showSuccessSnackbar"
        color="success"
        timeout="3000"
        bottom
    >
      <v-icon left>mdi-check-circle</v-icon>
      {{ successMessage }}
    </v-snackbar>

    <v-snackbar
        v-model="showErrorSnackbar"
        color="error"
        timeout="5000"
        bottom
    >
      <v-icon left>mdi-alert-circle</v-icon>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="showErrorSnackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Notifications',
  data() {
    return {
      loading: true,
      preferences: {
        send_campaign_email: false,
        send_campaign_push: false,
        send_credit_buy_voucher_email: false,
        send_credit_buy_voucher_push: false,
        send_notification_email: false,
        send_notification_push: false,
        send_notification_sms: false,
        send_parking_voucher_email: false,
        send_parking_voucher_push: false,
        send_credit_warning_email: false,
        send_credit_warning_push: false,
        send_notification_time_one_minutes_push: false,
        send_notification_time_five_minutes_push: false,
        send_notification_time_ten_minutes_push: false,
        send_notification_time_fifteen_minutes_push: false,
        send_notification_time_thirty_minutes_push: false,
        send_regularization_push: false,
        send_regularization_email: false,
      },
      notificationsConfig: [],
      updatingPreference: null,
      showSuccessSnackbar: false,
      showErrorSnackbar: false,
      successMessage: '',
      errorMessage: '',
    };
  },
  computed: {
    userData() {
      return this.$store.getters['user/userData'];
    },
  },
  mounted() {
    this.fetchUserData();
  },
  methods: {
    async fetchUserData() {
      this.loading = true;

      try {
        const { data } = await axios.get('/api/user');

        this.loadPreferencesFromUser(data);
        this.loadNotificationsConfig(data);
      } catch (error) {
        this.handleError('Erro ao carregar dados de notificações');
        console.error('Error fetching user data:', error);
      } finally {
        this.loading = false;
      }
    },
    loadPreferencesFromUser(userData) {
      const preferencesKeys = Object.keys(this.preferences);

      preferencesKeys.forEach(key => {
        if (userData.hasOwnProperty(key)) {
          this.preferences[key] = userData[key];
        }
      });
    },
    loadNotificationsConfig(userData) {
      if (userData.config && userData.config.notificationsConfig) {
        this.notificationsConfig = userData.config.notificationsConfig;
      } else {
        this.notificationsConfig = [];
      }
    },
    async updatePreference(preferenceKey, newValue) {
      this.updatingPreference = preferenceKey;
      const previousValue = this.preferences[preferenceKey];

      try {
        await axios.put('/api/users/me/notification/preference', {
          [preferenceKey]: newValue,
        });

        this.updateUserStore(preferenceKey, newValue);
        this.showSuccess('Preferência atualizada com sucesso');
      } catch (error) {
        this.preferences[preferenceKey] = previousValue;
        this.handleError('Não foi possível atualizar a preferência. Tente novamente.');
        console.error('Error updating preference:', error);
      } finally {
        this.updatingPreference = null;
      }
    },
    updateUserStore(preferenceKey, value) {
      if (this.$store.dispatch) {
        this.$store.dispatch('user/setUser', {
          [preferenceKey]: value,
        });
      }
    },
    showSuccess(message) {
      this.successMessage = message;
      this.showSuccessSnackbar = true;
    },
    handleError(message) {
      this.errorMessage = message;
      this.showErrorSnackbar = true;
    },
    handleBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.title-content {
  font-size: 28px;
  font-family: Roboto-Black, sans-serif;
  color: #595959;
  margin-bottom: 20px;
}

.form-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.notification-category {
  margin-bottom: 32px;
}

.notification-category:last-child {
  margin-bottom: 16px;
}

.category-title {
  font-size: 18px;
  font-weight: 600;
  color: #424242;
  text-align: center;
  margin-bottom: 8px;
}

.notification-option {
  padding: 8px 0;
}

.btn-cancel {
  text-transform: capitalize !important;
  background: #f5f5f5 !important;
  color: #595959 !important;
  font-family: Roboto-Medium, sans-serif !important;
  min-width: 120px;
  height: 43px !important;
  border-radius: 4px !important;
  padding: 8px 24px !important;
}

.btn-cancel:hover {
  background: #e0e0e0 !important;
}

@media only screen and (max-width: 600px) {
  .title-content {
    font-size: 24px;
  }

  .category-title {
    font-size: 16px;
  }

  .btn-cancel {
    width: 100%;
    margin: 0 !important;
  }
}
</style>
